import type { fabric } from 'fabric';
import { callRemoveBackground } from '@/lib/photoStudioAi';

/**
 * Client-side background removal (offline / non-HTTP fallback).
 * Flood-fills from the four corners through similar colors → transparency.
 * Works best on photos with a fairly flat backdrop (studio, sky, wall).
 */
export function clientRemoveNearBackground(
  image: HTMLImageElement | HTMLCanvasElement,
  tolerance = 48
): string {
  const srcW =
    'naturalWidth' in image ? image.naturalWidth || image.width : image.width;
  const srcH =
    'naturalHeight' in image ? image.naturalHeight || image.height : image.height;

  const maxEdge = 1200;
  const scale = Math.min(1, maxEdge / Math.max(srcW, srcH));
  const w = Math.max(1, Math.round(srcW * scale));
  const h = Math.max(1, Math.round(srcH * scale));

  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas unsupported');

  ctx.drawImage(image, 0, 0, w, h);
  const data = ctx.getImageData(0, 0, w, h);
  const px = data.data;
  const mask = new Uint8Array(w * h);

  const colorNear = (i: number, r: number, g: number, b: number) => {
    const dr = px[i] - r;
    const dg = px[i + 1] - g;
    const db = px[i + 2] - b;
    return dr * dr + dg * dg + db * db <= tolerance * tolerance;
  };

  const flood = (sx: number, sy: number) => {
    const si = (sy * w + sx) * 4;
    if (px[si + 3] < 8) return;
    const sr = px[si];
    const sg = px[si + 1];
    const sb = px[si + 2];
    const visited = new Uint8Array(w * h);
    const queue = new Int32Array(w * h);
    let qh = 0;
    let qt = 0;
    const enqueue = (x: number, y: number) => {
      if (x < 0 || y < 0 || x >= w || y >= h) return;
      const idx = y * w + x;
      if (visited[idx]) return;
      visited[idx] = 1;
      const i = idx * 4;
      if (px[i + 3] < 8) return;
      if (!colorNear(i, sr, sg, sb)) return;
      queue[qt++] = idx;
    };
    enqueue(sx, sy);
    while (qh < qt) {
      const idx = queue[qh++];
      mask[idx] = 1;
      const x = idx % w;
      const y = (idx / w) | 0;
      enqueue(x + 1, y);
      enqueue(x - 1, y);
      enqueue(x, y + 1);
      enqueue(x, y - 1);
    }
  };

  flood(0, 0);
  flood(w - 1, 0);
  flood(0, h - 1);
  flood(w - 1, h - 1);

  const full = document.createElement('canvas');
  full.width = srcW;
  full.height = srcH;
  const fctx = full.getContext('2d', { willReadFrequently: true });
  if (!fctx) throw new Error('Canvas unsupported');
  fctx.drawImage(image, 0, 0, srcW, srcH);
  const fullData = fctx.getImageData(0, 0, srcW, srcH);
  const fp = fullData.data;

  for (let y = 0; y < srcH; y++) {
    const my = Math.min(h - 1, Math.round(y * scale));
    for (let x = 0; x < srcW; x++) {
      const mx = Math.min(w - 1, Math.round(x * scale));
      if (mask[my * w + mx]) {
        fp[(y * srcW + x) * 4 + 3] = 0;
      }
    }
  }

  fctx.putImageData(fullData, 0, 0);
  return full.toDataURL('image/png');
}

export type RemoveBgResult = {
  mode: 'cloud' | 'client';
  creditsRemaining?: number;
  creditsLimit?: number;
};

async function tryCloudRemoveBackground(imageUrl: string): Promise<{
  url: string;
  creditsRemaining?: number;
  creditsLimit?: number;
} | null> {
  if (!imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
    return null;
  }
  try {
    const res = await callRemoveBackground({ imageUrl });
    if (!res.processedImageUrl) return null;
    return {
      url: res.processedImageUrl,
      creditsRemaining: res.credits?.remaining,
      creditsLimit: res.credits?.limit,
    };
  } catch (err) {
    // Free preview: never block on quota — fall back to client remove-bg
    // TODO(subscription): re-throw credits/Upgrade errors when billing ships
    // if (err instanceof Error && /credits|exhausted|Upgrade|Sign in/i.test(err.message)) {
    //   throw err;
    // }
    void err;
    return null;
  }
}

/**
 * Remove background from a Fabric image.
 * Prefers Cloud Function for public HTTP URLs; falls back to client punch-out.
 */
export async function removeBackgroundFromImage(
  canvas: fabric.Canvas,
  image: fabric.Image
): Promise<RemoveBgResult> {
  const el = image.getElement() as HTMLImageElement;
  const src =
    typeof image.getSrc === 'function' ? image.getSrc() : el.currentSrc || el.src;

  const cloud = src ? await tryCloudRemoveBackground(src) : null;
  const nextUrl = cloud?.url || clientRemoveNearBackground(el);

  await new Promise<void>((resolve) => {
    image.setSrc(
      nextUrl,
      () => {
        image.setCoords();
        canvas.requestRenderAll();
        resolve();
      },
      { crossOrigin: 'anonymous' }
    );
  });

  return cloud
    ? {
        mode: 'cloud',
        creditsRemaining: cloud.creditsRemaining,
        creditsLimit: cloud.creditsLimit,
      }
    : { mode: 'client' };
}
